// src/pages/personal/analytics/CustomerSection/components/CustomerMetrics.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MetricCard } from '@/components/ui/Layout';
import { 
  formatNumber, 
  formatPercent, 
  formatPrice 
} from '@/utils/Formatters';
import { LOCALES } from '@/utils/constants';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';

// Типы для метрик
type CustomerMetricKey = 'total_clients' | 'new_clients' | 'arpu' | 'retention_rate' | 'ltv' | 'aov' | 'pf';

interface Metric {
  value: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
}

interface CustomerMetricsProps {
  data?: Record<string, Metric>; // Сохраняем совместимость со старым интерфейсом
  metrics?: Record<CustomerMetricKey, Metric>; // Поддерживаем новый интерфейс
  onSelectMetric?: (metric: CustomerMetricKey) => void;
  selectedMetric?: CustomerMetricKey;
  isLoading?: boolean;
}

const CustomerMetrics: React.FC<CustomerMetricsProps> = ({ 
  data, // Поддержка старой версии
  metrics, // Поддержка новой версии
  onSelectMetric, 
  selectedMetric, 
  isLoading = false 
}) => {
  const { t, i18n } = useTranslation('customer');
  const { currencySymbol } = useBusinessCurrency();
  const currentLocale = i18n.language || LOCALES.DEFAULT;
  
  // Для совместимости используем либо metrics, либо data
  // Явно указываем тип для TypeScript
  const metricsData: Record<string, Metric> = metrics || data || {};
  
  // Список ключевых метрик для отображения, включая новые метрики
  const metricKeys: CustomerMetricKey[] = ['total_clients', 'new_clients', 'arpu', 'retention_rate', 'ltv', 'aov', 'pf'];
  
  // Функция для форматирования значения в зависимости от типа метрики
  const formatMetricValue = (key: CustomerMetricKey, value: number): string => {
    if (isLoading) return '-';
    
    switch (key) {
      case 'arpu':
      case 'ltv':
      case 'aov':
        return formatPrice(value, currencySymbol, currentLocale);
      case 'retention_rate':
        // Используем форматтер для процентов без деления на 100
        return formatPercent(value, 1, true, currentLocale);
      case 'pf':
        return formatNumber(value, 2, true, currentLocale);
      default:
        return formatNumber(value, 0, true, currentLocale);
    }
  };
  
  // Получаем названия метрик с учетом i18n
  const getMetricTranslation = (key: CustomerMetricKey): string => {
    const translations: Record<CustomerMetricKey, string> = {
      total_clients: t('metrics.total_clients'),
      new_clients: t('metrics.new_clients'),
      arpu: t('metrics.arpu'),
      retention_rate: t('metrics.retention_rate'),
      ltv: t('metrics.ltv'),
      aov: t('metrics.aov'),
      pf: t('metrics.pf')
    };
    
    return translations[key] || key;
  };
  
  return (
    <div className="w-full">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 w-full">
        {metricKeys.map((key) => {
          // Проверяем наличие ключа в объекте перед доступом к нему
          if (!(key in metricsData)) return null;
          
          // Получаем метрику безопасно, с явным приведением типа
          const metric = metricsData[key] as Metric;
          const value = metric?.value ?? 0;
          const change = metric?.change ?? 0;
          const trend = metric?.trend ?? 'neutral';
          
          return (
            <MetricCard
              key={key}
              title={getMetricTranslation(key)}
              value={isLoading ? '-' : formatMetricValue(key, value)}
              trend={trend}
              trendValue={change} // Передаем числовое значение
              isSelected={selectedMetric === key}
              onClick={onSelectMetric ? () => onSelectMetric(key) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};

export default CustomerMetrics;